import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { CheckIcon, MinusIcon } from '@phosphor-icons/react';
import { COMPARE_ROWS, changeSummary, costText, whatYouGet } from './planCompareModel';
import { planText, tabName } from './planCompareText';
import { hasCredits, lastsCell, leftLine, spendLine } from '../../utils/creditsModel';
import './sandbox.css';
import './PlanCompare.css';

/**
 * "Change plan": the plans side by side (GET /api/v1/me/plans). On a wide screen a table, plans as columns, the
 * person's own highlighted; at phone width one card per plan. Each plan says what it adds or takes away against the
 * person's plan ("What you get") and has its action: the person's own plan and a plan already asked for are not
 * actions; any other opens the confirmation (PlanConfirm) through `onChoose(plan)`.
 */

function Cell({ cell, t, lang }) {
  if (cell.tabs) {
    return <span title={cell.tabs.map((id) => tabName(id, lang)).join(', ')}>{cell.text}</span>;
  }
  if (cell.check === undefined) { return <span className={cell.strong ? 'sbx-pc-strong' : undefined} title={cell.title}>{cell.text}</span>; }
  if (!cell.check) {
    return <span className="sbx-pc-no"><MinusIcon aria-hidden size={14} /><span className="sbx-sr-only">{t('notIncluded')}</span></span>;
  }
  return (
    <span className="sbx-pc-yes">
      <CheckIcon aria-hidden size={14} weight="bold" />
      {cell.text ? <span>{cell.text}</span> : <span className="sbx-sr-only">{t('included')}</span>}
    </span>
  );
}

Cell.propTypes = { cell: PropTypes.object.isRequired, t: PropTypes.func.isRequired, lang: PropTypes.string };

function GetList({ plan, lang }) {
  return (
    <ul className="sbx-pc-get">
      {whatYouGet(plan, lang).map((line) => <li className={`sbx-pc-${line.tone}`} key={line.text}>{line.text}</li>)}
    </ul>
  );
}

GetList.propTypes = { plan: PropTypes.object.isRequired, lang: PropTypes.string };

function PlanAction({ plan, onChoose, t, busy }) {
  if (plan.current) { return <Button block disabled>{t('yourPlan')}</Button>; }
  if (plan.requested) { return <Button block disabled>{t('requested')}</Button>; }
  if (!plan.canChange) { return null; }
  const up = plan.vsCurrent?.direction === 'up';
  return (
    <Button aria-label={t('ask', { plan: plan.name })} block disabled={busy} onClick={() => onChoose(plan)} type={up ? 'primary' : 'default'}>
      {t('ask', { plan: plan.name })}
    </Button>
  );
}

PlanAction.propTypes = { plan: PropTypes.object.isRequired, onChoose: PropTypes.func.isRequired, t: PropTypes.func.isRequired, busy: PropTypes.bool };

function Badges({ plan, t }) {
  return (
    <>
      {plan.current && <span className="sbx-pc-badge current">{t('yourPlan')}</span>}
      {plan.requested && <span className="sbx-pc-badge requested">{t('requested')}</span>}
    </>
  );
}

Badges.propTypes = { plan: PropTypes.object.isRequired, t: PropTypes.func.isRequired };

export function PlanCompareTable({ catalog, onChoose, lang = undefined, phone = false, busy = false, credits = null }) {
  const t = planText(lang);
  const plans = catalog?.plans || [];
  // "Your credits last": the person's credits against each plan's cost per hour (GET /api/v1/me/credits); nothing without them.
  const lasts = (plan) => (hasCredits(credits) ? lastsCell(plan, credits, lang) : null);
  // "Credits a month" only when some plan grants credits (plans saved before grants have none).
  const rows = COMPARE_ROWS.filter((row) => row.key !== 'monthlyCredits' || plans.some((plan) => typeof plan.monthlyCredits === 'number'));
  if (phone) {
    return (
      <div className="sbx-pc-cards">
        {plans.map((plan) => (
          <section aria-label={plan.name} className={`sbx-pc-card${plan.current ? ' is-current' : ''}`} key={plan.name}>
            <header className="sbx-pc-card-head">
              <span className="sbx-pc-name">{plan.name}</span>
              <Badges plan={plan} t={t} />
              <span className="sbx-pc-card-cost">{costText(plan.costPerHour, t)}</span>
            </header>
            <GetList lang={lang} plan={plan} />
            <dl className="sbx-pc-dl">
              {lasts(plan) && (
                <div>
                  <dt>{t('rowLasts')}</dt>
                  <dd><Cell cell={lasts(plan)} lang={lang} t={t} /></dd>
                </div>
              )}
              {rows.filter((row) => row.key !== 'cost').map((row) => (
                <div key={row.key}>
                  <dt>{t(row.label)}</dt>
                  <dd><Cell cell={row.cell(plan, t)} lang={lang} t={t} /></dd>
                </div>
              ))}
            </dl>
            <PlanAction busy={busy} onChoose={onChoose} plan={plan} t={t} />
          </section>
        ))}
      </div>
    );
  }
  return (
    <div className="sbx-pc-wrap">
      <table className="sbx-pc-table">
        <caption className="sbx-sr-only">{t('title')}</caption>
        <thead>
          <tr>
            <td />
            {plans.map((plan) => (
              <th className={plan.current ? 'is-current' : undefined} key={plan.name} scope="col">
                <span className="sbx-pc-name">{plan.name}</span>
                <Badges plan={plan} t={t} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="sbx-pc-get-row">
            <th scope="row">{t('whatYouGet')}</th>
            {plans.map((plan) => <td className={plan.current ? 'is-current' : undefined} key={plan.name}><GetList lang={lang} plan={plan} /></td>)}
          </tr>
          {rows.map((row) => (
            <React.Fragment key={row.key}>
              <tr>
                <th scope="row">
                  {t(row.label)}
                  {row.hint && <span className="sbx-pc-hint">{t(row.hint)}</span>}
                </th>
                {plans.map((plan) => (
                  <td className={plan.current ? 'is-current' : undefined} key={plan.name}><Cell cell={row.cell(plan, t)} lang={lang} t={t} /></td>
                ))}
              </tr>
              {row.key === 'cost' && hasCredits(credits) && (
                <tr className="sbx-pc-lasts">
                  <th scope="row">
                    {t('rowLasts')}
                    <span className="sbx-pc-hint">{t('rowLastsHint')}</span>
                  </th>
                  {plans.map((plan) => (
                    <td className={plan.current ? 'is-current' : undefined} key={plan.name}>{lasts(plan)?.text || '—'}</td>
                  ))}
                </tr>
              )}
            </React.Fragment>
          ))}
          <tr className="sbx-pc-actions">
            <td />
            {plans.map((plan) => (
              <td className={plan.current ? 'is-current' : undefined} key={plan.name}><PlanAction busy={busy} onChoose={onChoose} plan={plan} t={t} /></td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

PlanCompareTable.propTypes = { catalog: PropTypes.object, onChoose: PropTypes.func.isRequired, lang: PropTypes.string, phone: PropTypes.bool, busy: PropTypes.bool, credits: PropTypes.object };

/** Above the table: what is left of the person's credits, what they spent and how long it lasts at this rate. */
export function CreditsSummary({ credits, lang = undefined }) {
  const t = planText(lang);
  const left = leftLine(credits, lang);
  if (!left) { return null; }
  const spend = spendLine(credits, lang);
  return (
    <div className="sbx-pc-credits" role="note">
      <strong>{t('creditsTitle')}</strong>
      <span>{left}</span>
      {spend && <span className="sbx-pc-credits-spend">{spend}</span>}
      <span className="sbx-pc-credits-note">{t('creditsNoteChange')}</span>
    </div>
  );
}

CreditsSummary.propTypes = { credits: PropTypes.object, lang: PropTypes.string };

/** The rates the estimate uses, under the table. */
export function EstimateNote({ catalog, lang = undefined }) {
  const t = planText(lang);
  const rates = catalog?.credits?.rates;
  return (
    <p className="sbx-pc-note">
      {t('estimateNote')}
      {rates && <span className="sbx-pc-rates">{t('rates', { cpu: rates.cpuHour, mem: rates.memoryGbHour, disk: rates.diskGbMonth })}</span>}
    </p>
  );
}

EstimateNote.propTypes = { catalog: PropTypes.object, lang: PropTypes.string };

/**
 * The confirmation before asking for `plan`: what happens (the admin decides, applies on the next wake), the cost per
 * hour from → to, what the person gets and everything that shrinks or goes away; the optional reason and the send.
 */
export function PlanConfirm({ plan, catalog, lang = undefined, reason, onReason, onBack, onSend, busy = false, error = null }) {
  const t = planText(lang);
  const summary = changeSummary(plan, catalog, lang);
  return (
    <form className="sbx-pc-confirm" noValidate onSubmit={(event) => { event.preventDefault(); onSend(); }}>
      <h3 className="sbx-pc-confirm-title">{t('confirmTitle', { plan: plan.name })}</h3>
      <p>{t('confirmAsk')}</p>
      <p className="sbx-pc-confirm-cost">{summary.cost}</p>
      {summary.gains.length > 0 && <p className="sbx-pc-gain">{t('confirmGains', { list: summary.gains.join(', ') })}</p>}
      {summary.shrinks.length > 0 && (
        <div className="sbx-pc-shrinks" role="note">
          <strong>{t('confirmShrinks')}</strong>
          <ul>{summary.shrinks.map((line) => <li key={line}>{line}</li>)}</ul>
          <span>{t('confirmShrinksNote')}</span>
        </div>
      )}
      {summary.replaces && <p className="sbx-plan-replace" role="note">{t('confirmReplace', { plan: summary.replaces })}</p>}
      <label className="sbx-plan-label" htmlFor="sbx-plan-reason">{t('reason')}</label>
      <textarea
        className="sbx-plan-reason"
        disabled={busy}
        id="sbx-plan-reason"
        maxLength={1000}
        onChange={(event) => onReason(event.target.value)}
        placeholder={t('reasonPlaceholder')}
        rows={3}
        value={reason}
      />
      {error && <p className="sbx-plan-error" role="alert">{error}</p>}
      <div className="sbx-plan-actions">
        <Button disabled={busy} onClick={onBack}>{t('back')}</Button>
        <Button htmlType="submit" loading={busy} type="primary">{busy ? t('sending') : t('send', { plan: plan.name })}</Button>
      </div>
    </form>
  );
}

PlanConfirm.propTypes = {
  plan: PropTypes.object.isRequired,
  catalog: PropTypes.object,
  lang: PropTypes.string,
  reason: PropTypes.string.isRequired,
  onReason: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  busy: PropTypes.bool,
  error: PropTypes.string,
};
